import { useState, useRef, useCallback } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORIES = [
  { id: "linehaul",       label: "Linehaul",        color: "#3b82f6" },
  { id: "fuel_surcharge", label: "Fuel Surcharge",  color: "#f59e0b" },
  { id: "detention",      label: "Detention",       color: "#ef4444" },
  { id: "lumper",         label: "Lumper",          color: "#8b5cf6" },
  { id: "accessorial",    label: "Accessorial",     color: "#06b6d4" },
  { id: "layover",        label: "Layover",         color: "#f97316" },
  { id: "tonu",           label: "TONU",            color: "#ec4899" },
  { id: "stop_off",       label: "Stop-Off",        color: "#14b8a6" },
  { id: "other",          label: "Other",           color: "#6b7280" },
];

const DOC_TYPE_LABELS = {
  freight_invoice:   "Freight Invoice",
  bol:               "Bill of Lading",
  rate_confirmation: "Rate Confirmation",
  fuel_surcharge:    "Fuel Surcharge",
  lumper:            "Lumper Receipt",
  detention:         "Detention Notice",
  delivery_receipt:  "Delivery Receipt",
  purchase_order:    "Purchase Order",
  general_invoice:   "General Invoice",
};

const UNITS         = ["load", "mile", "hour", "flat", "cwt", "day", "trip"];
const PAYMENT_TERMS = ["Net 15", "Net 30", "Net 45", "Net 60", "Due on Receipt", "COD"];

const uid  = () => `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const r2   = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmtc = (n) => `$${r2(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const invNum = () => `INV-${new Date().getFullYear()}-${Date.now().toString().slice(-5)}`;

const blankItem = () => ({
  id: uid(), description: "", category: "other",
  quantity: 1, unit: "flat", rate: 0, amount: 0, taxable: false,
});

const blankInvoice = () => ({
  documentType: "freight_invoice",
  logoBase64:   null,
  vendor:  { name: "", address: "", city: "", state: "", zip: "", phone: "", email: "", mc: "", dot: "" },
  billTo:  { name: "", address: "", city: "", state: "", zip: "", phone: "", email: "" },
  invoice: { number: invNum(), date: today(), dueDate: "", poNumber: "", bolNumber: "", loadNumber: "" },
  lineItems:    [blankItem()],
  totals:       { taxRate: 0, discountAmount: 0 },
  notes:        "",
  paymentTerms: "Net 30",
  aiSuggestions: [],
});

const getToken = () => localStorage.getItem("auth_token");

// ─── Reusable UI atoms ────────────────────────────────────────────────────────
function Badge({ children, color = "#6b7280" }) {
  return (
    <span style={{ background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
      {children}
    </span>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 5 }}>
        {label}{required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", hasError, style = {} }) {
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: "100%", background: "#f8fafc", border: `1.5px solid ${hasError ? "#ef4444" : "#e2e8f0"}`, color: "#0f172a", fontFamily: "inherit", fontSize: 13, padding: "9px 12px", borderRadius: 8, outline: "none", boxSizing: "border-box", ...style }}
      onFocus={e => { e.target.style.borderColor = hasError ? "#ef4444" : "#6366f1"; e.target.style.boxShadow = "0 0 0 3px " + (hasError ? "#ef444420" : "#6366f120"); }}
      onBlur={e  => { e.target.style.borderColor = hasError ? "#ef4444" : "#e2e8f0"; e.target.style.boxShadow = "none"; }}
    />
  );
}

function SectionCard({ title, icon, children, accent = "#6366f1" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, marginBottom: 20, overflow: "hidden" }}>
      <div style={{ background: `linear-gradient(135deg, ${accent}08, ${accent}04)`, borderBottom: "1px solid #e2e8f0", padding: "14px 20px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", letterSpacing: -0.3 }}>{title}</span>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function InvoiceGenerator() {
  const [view,          setView]          = useState("landing");
  const [data,          setData]          = useState(blankInvoice());
  const [aiMode,        setAiMode]        = useState("text");
  const [aiText,        setAiText]        = useState("");
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiError,       setAiError]       = useState("");
  const [aiConfidence,  setAiConfidence]  = useState(null);
  const [dragOver,      setDragOver]      = useState(false);
  const [fileName,      setFileName]      = useState("");
  const [submitting,    setSubmitting]    = useState(false);
  const [submitError,   setSubmitError]   = useState("");
  const [finalInvoice,  setFinalInvoice]  = useState(null);
  const [fieldErrors,   setFieldErrors]   = useState({});
  const [copied,        setCopied]        = useState(false);
  const [savedHistory,  setSavedHistory]  = useState(false);
  const fileRef    = useRef();
  const logoRef    = useRef();

  // ── Computed totals ──────────────────────────────────────────────────────
  const subtotal       = r2(data.lineItems.reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const taxableAmt     = r2(data.lineItems.filter(i => i.taxable).reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const taxAmount      = r2(taxableAmt * ((Number(data.totals.taxRate) || 0) / 100));
  const discountAmount = r2(Number(data.totals.discountAmount) || 0);
  const total          = r2(subtotal + taxAmount - discountAmount);

  // ── Data helpers ─────────────────────────────────────────────────────────
  const setField = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateItem = (id, field, value) => {
    setData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (field === "quantity" || field === "rate") {
          updated.amount = r2(
            (Number(field === "quantity" ? value : item.quantity) || 1) *
            (Number(field === "rate" ? value : item.rate) || 0)
          );
        }
        return updated;
      }),
    }));
  };

  const addItem    = ()    => setData(prev => ({ ...prev, lineItems: [...prev.lineItems, blankItem()] }));
  const removeItem = (id)  => setData(prev => ({ ...prev, lineItems: prev.lineItems.filter(i => i.id !== id) }));

  // ── Logo upload ──────────────────────────────────────────────────────────
  const handleLogoUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setField("logoBase64", e.target.result);
    reader.readAsDataURL(file);
  };

  // ── Copy invoice number ──────────────────────────────────────────────────
  const copyInvNum = () => {
    navigator.clipboard.writeText(data.invoice.number).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Client-side validation ───────────────────────────────────────────────
  const validate = () => {
    const errors = {};
    if (!data.vendor.name.trim())      errors["vendor.name"]   = true;
    if (!data.billTo.name.trim())      errors["billTo.name"]   = true;
    if (!data.invoice.number.trim())   errors["invoice.number"] = true;
    if (!data.invoice.date.trim())     errors["invoice.date"]   = true;
    data.lineItems.forEach((item, i) => {
      if (!item.description.trim()) errors[`lineItems.${i}.description`] = true;
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── AI Parse ─────────────────────────────────────────────────────────────
  const handleAiParse = useCallback(async (fileBase64, fileMimeType) => {
    setAiLoading(true); setAiError(""); setAiConfidence(null);
    try {
      const body = fileBase64 ? { file: fileBase64, mimeType: fileMimeType } : { text: aiText };
      const res  = await fetch(`${BACKEND_URL}/api/invoice/parse`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Parse failed");

      const parsed = json.data;
      setAiConfidence(parsed.confidence);
      setData({
        documentType:  parsed.documentType  || "freight_invoice",
        logoBase64:    null,
        vendor:        { ...blankInvoice().vendor,  ...(parsed.vendor  || {}) },
        billTo:        { ...blankInvoice().billTo,  ...(parsed.billTo  || {}) },
        invoice:       { ...blankInvoice().invoice, ...(parsed.invoice || {}) },
        lineItems:     parsed.lineItems?.length ? parsed.lineItems : [blankItem()],
        totals:        { taxRate: parsed.totals?.taxRate || 0, discountAmount: parsed.totals?.discountAmount || 0 },
        notes:         parsed.notes        || "",
        paymentTerms:  parsed.paymentTerms || "Net 30",
        aiSuggestions: parsed.aiSuggestions || [],
      });
      setView("build");
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  }, [aiText]);

  const handleFileUpload = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      handleAiParse(e.target.result.split(",")[1], file.type);
    };
    reader.readAsDataURL(file);
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) {
      setSubmitError("Please fill in all required fields highlighted in red.");
      return;
    }
    setSubmitting(true); setSubmitError("");
    try {
      const res  = await fetch(`${BACKEND_URL}/api/invoice/generate`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: JSON.stringify({
          ...data,
          totals: { ...data.totals, subtotal, taxAmount, discountAmount, total },
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.details?.join(", ") || json.error);
      setFinalInvoice({ ...json.invoice, logoBase64: data.logoBase64 });
      setView("preview");
      setSavedHistory(false);
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Save invoice to history after generation ─────────────────────────────
  const saveToHistory = async () => {
    if (!finalInvoice || !getToken()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/history/invoice`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          Authorization:   `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          invoice_number:  finalInvoice.invoice?.number,
          document_type:   finalInvoice.documentType,
          vendor_name:     finalInvoice.vendor?.name,
          bill_to_name:    finalInvoice.billTo?.name,
          total:           finalInvoice.totals?.total,
          status:          "finalized",
        }),
      });
      if (res.ok) setSavedHistory(true);
    } catch {}
  };

  const getCatColor = (cat) => CATEGORIES.find(c => c.id === cat)?.color || "#6b7280";

  // ── Styles ───────────────────────────────────────────────────────────────
  const S = {
    page:  { minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", color: "#0f172a" },
    btn:   (bg, color = "#fff") => ({ background: bg, color, border: "none", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "11px 22px", borderRadius: 9, cursor: "pointer", letterSpacing: 0.2 }),
    ghost: { background: "transparent", border: "1.5px solid #e2e8f0", color: "#64748b", fontFamily: "inherit", fontSize: 13, fontWeight: 600, padding: "10px 20px", borderRadius: 9, cursor: "pointer" },
    danger:{ background: "transparent", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "4px 8px", borderRadius: 5 },
  };

  // ─────────────────────────────────────────────────────────────────────────
  // LANDING VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (view === "landing") return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 16px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        textarea { font-family: inherit; }
        .hover-lift:hover { opacity: 0.92; transform: translateY(-1px); }
        .drop-zone { border: 2px dashed #c7d2fe; border-radius: 14px; padding: 40px; text-align: center; cursor: pointer; transition: all 0.2s; background: #f8f9ff; }
        .drop-zone.active, .drop-zone:hover { border-color: #6366f1; background: #eef2ff; }
        .tab-btn { padding: 9px 20px; border-radius: 8px; border: none; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .tab-btn.active { background: #6366f1; color: #fff; }
        .tab-btn:not(.active) { background: transparent; color: #64748b; }
      `}</style>

      <div style={{ width: "100%", maxWidth: 680 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 60, height: 60, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: 16, fontSize: 28, marginBottom: 16, boxShadow: "0 8px 24px #6366f140" }}>🧾</div>
          <h1 style={{ fontSize: 32, fontWeight: 800, margin: "0 0 8px", letterSpacing: -1 }}>Invoice Generator</h1>
          <p style={{ fontSize: 15, color: "#64748b", margin: 0 }}>AI-powered parsing · Manual entry · Transportation-ready</p>
        </div>

        <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 4px 32px #0000000d", border: "1px solid #e2e8f0", overflow: "hidden" }}>

          {/* AI Section */}
          <div style={{ padding: "28px 28px 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 20 }}>✨</span>
              <span style={{ fontWeight: 800, fontSize: 16 }}>AI Document Parser</span>
              <Badge color="#6366f1">Powered by Claude</Badge>
            </div>

            <div style={{ display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 10, padding: 4, marginBottom: 16, width: "fit-content" }}>
              <button className={`tab-btn${aiMode === "text" ? " active" : ""}`} onClick={() => setAiMode("text")}>📋 Paste Text</button>
              <button className={`tab-btn${aiMode === "file" ? " active" : ""}`} onClick={() => setAiMode("file")}>📎 Upload File</button>
            </div>

            {aiMode === "text" && (
              <div>
                <textarea
                  value={aiText} onChange={e => setAiText(e.target.value)}
                  placeholder={"Paste any document text here — invoice, BOL, rate confirmation, lumper receipt, detention notice...\n\nAI will identify the document type, extract all fields, and build the invoice automatically."}
                  style={{ width: "100%", minHeight: 160, background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: 14, fontSize: 13, color: "#0f172a", resize: "vertical", outline: "none", lineHeight: 1.6 }}
                />
                {aiError && <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginTop: 8 }}>⚠ {aiError}</div>}
                <button
                  style={{ ...S.btn("linear-gradient(135deg,#6366f1,#8b5cf6)"), width: "100%", marginTop: 10, padding: 14, fontSize: 14 }}
                  className="hover-lift"
                  onClick={() => handleAiParse(null, null)}
                  disabled={!aiText.trim() || aiLoading}
                >
                  {aiLoading ? "🔍 Analyzing document…" : "✨ Parse with AI"}
                </button>
              </div>
            )}

            {aiMode === "file" && (
              <div>
                <div
                  className={`drop-zone${dragOver ? " active" : ""}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
                  onClick={() => fileRef.current?.click()}
                >
                  <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: "none" }} onChange={e => handleFileUpload(e.target.files[0])} />
                  {aiLoading ? (
                    <div style={{ color: "#6366f1", fontWeight: 600 }}>🔍 Analyzing {fileName}…</div>
                  ) : fileName ? (
                    <div style={{ color: "#6366f1", fontWeight: 600 }}>✓ {fileName} — AI parsing…</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Drop file here or click to upload</div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>JPEG, PNG, WEBP, PDF · Max 10MB</div>
                    </>
                  )}
                </div>
                {aiError && <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginTop: 8 }}>⚠ {aiError}</div>}
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 28px" }}>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
          </div>

          <div style={{ padding: "0 28px 28px" }}>
            <button style={{ ...S.btn("#0f172a"), width: "100%", padding: 14, fontSize: 14 }} className="hover-lift" onClick={() => { setData(blankInvoice()); setView("build"); }}>
              ✏️ Build Invoice Manually
            </button>
            <p style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", margin: "10px 0 0" }}>Start from scratch with a blank invoice form</p>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 24 }}>
          {["🚚 Freight Invoices", "📋 Bills of Lading", "⛽ Fuel Surcharges", "🔧 Accessorials", "🏷️ Lumper Receipts", "⏱ Detention"].map(f => (
            <div key={f} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 99, padding: "5px 14px", fontSize: 12, color: "#64748b", fontWeight: 500 }}>{f}</div>
          ))}
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // BUILD VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (view === "build") return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        .hover-lift:hover { opacity: 0.9; transform: translateY(-1px); }
        .item-row:hover .del-btn { opacity: 1 !important; }
        input, select, textarea { font-family: inherit; }
      `}</style>

      {/* Top bar */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50, boxShadow: "0 1px 8px #0000000a" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button style={S.ghost} onClick={() => setView("landing")}>← Back</button>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Invoice Builder</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{DOC_TYPE_LABELS[data.documentType]}</div>
          </div>
          {aiConfidence && <Badge color="#6366f1">AI {Math.round(aiConfidence * 100)}% confident</Badge>}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Total: {fmtc(total)}</div>
          <button style={{ ...S.btn("linear-gradient(135deg,#6366f1,#8b5cf6)") }} className="hover-lift" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Generating…" : "Generate Invoice →"}
          </button>
        </div>
      </div>

      {/* AI suggestions banner */}
      {data.aiSuggestions?.length > 0 && (
        <div style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a", padding: "10px 24px", display: "flex", alignItems: "center", gap: 10 }}>
          <span>⚠️</span>
          <div style={{ fontSize: 12, color: "#92400e" }}><strong>AI Notes:</strong> {data.aiSuggestions.join(" · ")}</div>
        </div>
      )}

      {submitError && (
        <div style={{ background: "#fff5f5", borderBottom: "1px solid #fecaca", padding: "10px 24px", color: "#dc2626", fontSize: 13 }}>⚠ {submitError}</div>
      )}

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px 60px" }}>

        {/* Document type */}
        <SectionCard title="Document Type" icon="📄" accent="#6366f1">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            {Object.entries(DOC_TYPE_LABELS).map(([id, label]) => (
              <button key={id} onClick={() => setField("documentType", id)}
                style={{ padding: "10px 14px", borderRadius: 9, border: `1.5px solid ${data.documentType === id ? "#6366f1" : "#e2e8f0"}`, background: data.documentType === id ? "#eef2ff" : "#f8fafc", color: data.documentType === id ? "#6366f1" : "#64748b", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Vendor & Bill-To */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

          {/* Vendor */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ background: "linear-gradient(135deg,#6366f108,#6366f104)", borderBottom: "1px solid #e2e8f0", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span>🏢</span><span style={{ fontWeight: 700, fontSize: 14 }}>From (Vendor)</span></div>
              {/* Logo upload */}
              <div>
                <input ref={logoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleLogoUpload(e.target.files[0])} />
                <button onClick={() => logoRef.current?.click()}
                  style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 6, padding: "5px 10px", cursor: "pointer" }}>
                  {data.logoBase64 ? "✓ Logo uploaded" : "Upload Logo"}
                </button>
                {data.logoBase64 && (
                  <button onClick={() => setField("logoBase64", null)}
                    style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", marginLeft: 6 }}>✕</button>
                )}
              </div>
            </div>
            <div style={{ padding: 20 }}>
              {[["name","Company Name",true],["address","Address"],["city","City"],["state","State"],["zip","ZIP"],["phone","Phone"],["email","Email"],["mc","MC #"],["dot","DOT #"]].map(([f, lbl, req]) => (
                <Field key={f} label={lbl} required={req}>
                  <Input value={data.vendor[f]} onChange={v => setField(`vendor.${f}`, v)} placeholder={lbl} hasError={!!fieldErrors[`vendor.${f}`]} />
                </Field>
              ))}
            </div>
          </div>

          {/* Bill To */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ background: "linear-gradient(135deg,#0ea5e908,#0ea5e904)", borderBottom: "1px solid #e2e8f0", padding: "14px 20px", display: "flex", alignItems: "center", gap: 10 }}>
              <span>📬</span><span style={{ fontWeight: 700, fontSize: 14 }}>Bill To</span>
            </div>
            <div style={{ padding: 20 }}>
              {[["name","Company / Name",true],["address","Address"],["city","City"],["state","State"],["zip","ZIP"],["phone","Phone"],["email","Email"]].map(([f, lbl, req]) => (
                <Field key={f} label={lbl} required={req}>
                  <Input value={data.billTo[f]} onChange={v => setField(`billTo.${f}`, v)} placeholder={lbl} hasError={!!fieldErrors[`billTo.${f}`]} />
                </Field>
              ))}
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <SectionCard title="Invoice Details" icon="🔢" accent="#0ea5e9">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
            {/* Invoice number with copy button */}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 5 }}>
                Invoice Number <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <Input value={data.invoice.number} onChange={v => setField("invoice.number", v)} placeholder="INV-2026-00001" hasError={!!fieldErrors["invoice.number"]} />
                <button onClick={copyInvNum} title="Copy"
                  style={{ padding: "9px 12px", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap", color: copied ? "#22c55e" : "#64748b" }}>
                  {copied ? "✓" : "📋"}
                </button>
              </div>
            </div>
            {[["date","Invoice Date",true,"date"],["dueDate","Due Date","","date"],["poNumber","PO Number"],["bolNumber","BOL Number"],["loadNumber","Load Number"]].map(([f, lbl, req, type]) => (
              <Field key={f} label={lbl} required={!!req}>
                <Input value={data.invoice[f]} onChange={v => setField(`invoice.${f}`, v)} placeholder={lbl} type={type || "text"} hasError={!!fieldErrors[`invoice.${f}`]} />
              </Field>
            ))}
          </div>
        </SectionCard>

        {/* Line Items */}
        <SectionCard title="Line Items" icon="📦" accent="#f59e0b">
          <div style={{ display: "grid", gridTemplateColumns: "2fr 120px 80px 80px 90px 90px 80px 36px", gap: 8, padding: "0 0 8px", borderBottom: "1px solid #f1f5f9", marginBottom: 8 }}>
            {["Description","Category","Qty","Unit","Rate","Amount","Taxable",""].map((h, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 1, textTransform: "uppercase" }}>{h}</div>
            ))}
          </div>

          {data.lineItems.map((item, idx) => (
            <div key={item.id} className="item-row" style={{ display: "grid", gridTemplateColumns: "2fr 120px 80px 80px 90px 90px 80px 36px", gap: 8, marginBottom: 8, alignItems: "center" }}>
              <input
                value={item.description} onChange={e => updateItem(item.id, "description", e.target.value)}
                placeholder={`Line item ${idx + 1} description`}
                style={{ background: fieldErrors[`lineItems.${idx}.description`] ? "#fff5f5" : "#f8fafc", border: `1.5px solid ${fieldErrors[`lineItems.${idx}.description`] ? "#ef4444" : "#e2e8f0"}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: "#0f172a", outline: "none", fontFamily: "inherit", width: "100%" }}
              />
              <select value={item.category} onChange={e => updateItem(item.id, "category", e.target.value)}
                style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 6px", fontSize: 11, color: getCatColor(item.category), fontFamily: "inherit", fontWeight: 600, outline: "none", width: "100%" }}>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <input type="number" value={item.quantity} onChange={e => updateItem(item.id, "quantity", e.target.value)}
                style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 6px", fontSize: 13, textAlign: "center", fontFamily: "inherit", outline: "none", width: "100%" }} />
              <select value={item.unit} onChange={e => updateItem(item.id, "unit", e.target.value)}
                style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 4px", fontSize: 12, fontFamily: "inherit", outline: "none", width: "100%" }}>
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <input type="number" value={item.rate} onChange={e => updateItem(item.id, "rate", e.target.value)} placeholder="0.00"
                style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 6px", fontSize: 13, textAlign: "right", fontFamily: "inherit", outline: "none", width: "100%" }} />
              <input type="number" value={item.amount} onChange={e => updateItem(item.id, "amount", e.target.value)} placeholder="0.00"
                style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 8, padding: "8px 6px", fontSize: 13, fontWeight: 700, textAlign: "right", fontFamily: "inherit", outline: "none", color: "#92400e", width: "100%" }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <input type="checkbox" checked={item.taxable} onChange={e => updateItem(item.id, "taxable", e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: "#6366f1", cursor: "pointer" }} />
              </div>
              <button className="del-btn" onClick={() => removeItem(item.id)}
                style={{ ...S.danger, opacity: data.lineItems.length === 1 ? 0.2 : 0.4, transition: "opacity 0.15s" }}
                disabled={data.lineItems.length === 1}>×</button>
            </div>
          ))}

          <button onClick={addItem} style={{ background: "#f8fafc", border: "1.5px dashed #c7d2fe", color: "#6366f1", fontFamily: "inherit", fontSize: 12, fontWeight: 700, padding: 10, borderRadius: 9, cursor: "pointer", width: "100%", marginTop: 8 }}>
            + Add Line Item
          </button>
        </SectionCard>

        {/* Notes / Totals */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <SectionCard title="Notes & Payment Terms" icon="📝" accent="#14b8a6">
            <Field label="Payment Terms">
              <select value={data.paymentTerms} onChange={e => setField("paymentTerms", e.target.value)}
                style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", color: "#0f172a", fontFamily: "inherit", fontSize: 13, padding: "9px 12px", borderRadius: 8, outline: "none" }}>
                {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Notes">
              <textarea value={data.notes} onChange={e => setField("notes", e.target.value)}
                placeholder="Additional notes, instructions, or reference numbers..."
                style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#0f172a", minHeight: 90, resize: "vertical", outline: "none" }} />
            </Field>
          </SectionCard>

          {/* Summary */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ background: "linear-gradient(135deg,#0f172a,#1e293b)", padding: "14px 20px" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>💰 Invoice Summary</span>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: "#64748b" }}>Subtotal</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{fmtc(subtotal)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: "#64748b" }}>Tax Rate (%)</span>
                <input type="number" value={data.totals.taxRate} onChange={e => setField("totals.taxRate", e.target.value)}
                  style={{ width: 80, background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "6px 8px", fontSize: 13, textAlign: "right", fontFamily: "inherit", outline: "none" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: "#64748b" }}>Discount ($)</span>
                <input type="number" value={data.totals.discountAmount} onChange={e => setField("totals.discountAmount", e.target.value)}
                  style={{ width: 80, background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "6px 8px", fontSize: 13, textAlign: "right", fontFamily: "inherit", outline: "none" }} />
              </div>
              {taxAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: "#64748b" }}>Tax Amount</span>
                  <span style={{ fontSize: 13, color: "#64748b" }}>{fmtc(taxAmount)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: "#64748b" }}>Discount</span>
                  <span style={{ fontSize: 13, color: "#ef4444" }}>−{fmtc(discountAmount)}</span>
                </div>
              )}
              <div style={{ height: 1, background: "#e2e8f0", margin: "12px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 16, fontWeight: 800 }}>TOTAL DUE</span>
                <span style={{ fontSize: 24, fontWeight: 800, color: "#6366f1", letterSpacing: -1 }}>{fmtc(total)}</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button style={S.ghost} onClick={() => setView("landing")}>↺ Start Over</button>
          <button style={{ ...S.btn("linear-gradient(135deg,#6366f1,#8b5cf6)"), padding: "13px 32px", fontSize: 14 }} className="hover-lift" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Generating…" : "✓ Generate Invoice"}
          </button>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // PREVIEW VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (view === "preview") {
    const inv = finalInvoice;
    return (
      <div style={S.page}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          * { box-sizing: border-box; }
          .hover-lift:hover { opacity: 0.9; transform: translateY(-1px); }
          @media print {
            .no-print { display: none !important; }
            body { background: #fff; }
            .invoice-doc { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; }
          }
        `}</style>

        {/* Toolbar */}
        <div className="no-print" style={{ background: "#0f172a", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...S.ghost, borderColor: "#334155", color: "#94a3b8" }} onClick={() => setView("build")}>← Edit</button>
            <button style={{ ...S.btn("#1e293b") }} onClick={() => { setData(blankInvoice()); setView("landing"); }}>New Invoice</button>
          </div>
          <span style={{ fontWeight: 800, color: "#fff", fontSize: 15 }}>Invoice #{inv?.invoice?.number}</span>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={saveToHistory}
              style={{ ...S.btn(savedHistory ? "#166534" : "#1e293b"), borderColor: "#334155" }}
              disabled={savedHistory}
            >
              {savedHistory ? "✓ Saved to History" : "💾 Save to History"}
            </button>
            <button style={{ ...S.btn("#4ade80", "#14532d") }} className="hover-lift" onClick={() => window.print()}>🖨 Print / Save PDF</button>
          </div>
        </div>

        {/* Invoice document */}
        <div style={{ maxWidth: 860, margin: "32px auto", padding: "0 16px 60px" }}>
          <div className="invoice-doc" style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 40px #00000012", padding: "48px 52px" }}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36, paddingBottom: 28, borderBottom: "3px solid #0f172a" }}>
              <div>
                {inv?.logoBase64 && (
                  <img src={inv.logoBase64} alt="Company logo" style={{ maxHeight: 60, maxWidth: 180, objectFit: "contain", marginBottom: 10, display: "block" }} />
                )}
                <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -1, marginBottom: 4 }}>{inv?.vendor?.name || "Your Company"}</div>
                {inv?.vendor?.mc  && <div style={{ fontSize: 12, color: "#64748b" }}>MC# {inv.vendor.mc}</div>}
                {inv?.vendor?.dot && <div style={{ fontSize: 12, color: "#64748b" }}>DOT# {inv.vendor.dot}</div>}
                {inv?.vendor?.address && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{inv.vendor.address}{inv.vendor.city ? `, ${inv.vendor.city}` : ""}{inv.vendor.state ? `, ${inv.vendor.state}` : ""} {inv.vendor.zip}</div>}
                {inv?.vendor?.phone && <div style={{ fontSize: 12, color: "#64748b" }}>{inv.vendor.phone}</div>}
                {inv?.vendor?.email && <div style={{ fontSize: 12, color: "#64748b" }}>{inv.vendor.email}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#6366f1", fontWeight: 800, textTransform: "uppercase", marginBottom: 4 }}>{DOC_TYPE_LABELS[inv?.documentType]}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#6366f1", letterSpacing: -1 }}>#{inv?.invoice?.number}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Date: {inv?.invoice?.date}</div>
                {inv?.invoice?.dueDate    && <div style={{ fontSize: 12, color: "#64748b" }}>Due: {inv.invoice.dueDate}</div>}
                {inv?.invoice?.bolNumber  && <div style={{ fontSize: 12, color: "#64748b" }}>BOL: {inv.invoice.bolNumber}</div>}
                {inv?.invoice?.loadNumber && <div style={{ fontSize: 12, color: "#64748b" }}>Load: {inv.invoice.loadNumber}</div>}
                <div style={{ marginTop: 8 }}>
                  <Badge color="#22c55e">Finalized</Badge>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div style={{ marginBottom: 28, background: "#f8fafc", borderRadius: 10, padding: "16px 20px", display: "inline-block", minWidth: 220 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Bill To</div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{inv?.billTo?.name}</div>
              {inv?.billTo?.address && <div style={{ fontSize: 12, color: "#64748b" }}>{inv.billTo.address}</div>}
              {inv?.billTo?.city    && <div style={{ fontSize: 12, color: "#64748b" }}>{inv.billTo.city}{inv.billTo.state ? `, ${inv.billTo.state}` : ""} {inv.billTo.zip}</div>}
              {inv?.billTo?.email   && <div style={{ fontSize: 12, color: "#64748b" }}>{inv.billTo.email}</div>}
            </div>

            {/* Line items */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 24 }}>
              <thead>
                <tr style={{ background: "#0f172a" }}>
                  {["Description","Category","Qty","Unit","Rate","Amount"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, color: "#94a3b8", letterSpacing: 1.2, fontWeight: 700, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inv?.lineItems?.map((item, i) => (
                  <tr key={item.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "11px 12px", fontWeight: 500 }}>{item.description}</td>
                    <td style={{ padding: "11px 12px" }}><Badge color={getCatColor(item.category)}>{CATEGORIES.find(c => c.id === item.category)?.label || item.category}</Badge></td>
                    <td style={{ padding: "11px 12px", color: "#64748b" }}>{item.quantity}</td>
                    <td style={{ padding: "11px 12px", color: "#64748b" }}>{item.unit}</td>
                    <td style={{ padding: "11px 12px", fontFamily: "monospace", color: "#64748b" }}>{fmtc(item.rate)}</td>
                    <td style={{ padding: "11px 12px", fontFamily: "monospace", fontWeight: 700 }}>{fmtc(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 28 }}>
              <div style={{ minWidth: 260 }}>
                {[
                  ["Subtotal", fmtc(inv?.totals?.subtotal || 0)],
                  inv?.totals?.taxAmount > 0 && [`Tax (${inv?.totals?.taxRate}%)`, fmtc(inv?.totals?.taxAmount || 0)],
                  inv?.totals?.discountAmount > 0 && ["Discount", `−${fmtc(inv?.totals?.discountAmount || 0)}`],
                ].filter(Boolean).map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
                    <span style={{ color: "#64748b" }}>{l}</span>
                    <span style={{ fontFamily: "monospace" }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, padding: "12px 16px", background: "#0f172a", borderRadius: 10 }}>
                  <span style={{ fontWeight: 800, color: "#fff", fontSize: 14 }}>TOTAL DUE</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 800, color: "#4ade80", fontSize: 20 }}>{fmtc(inv?.totals?.total || 0)}</span>
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            {(inv?.notes || inv?.paymentTerms) && (
              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {inv?.paymentTerms && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Payment Terms</div>
                    <div style={{ fontSize: 13 }}>{inv.paymentTerms}</div>
                  </div>
                )}
                {inv?.notes && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Notes</div>
                    <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>{inv.notes}</div>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 28, paddingTop: 16, borderTop: "1px solid #f1f5f9", textAlign: "center", fontSize: 11, color: "#cbd5e1" }}>
              Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · Invoice ID: {inv?.id}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
